import React, { useEffect, useState } from 'react'
import { TRecipientInfo } from '@/src/modules/build-a-card/inputs/types'
import DeleteRecipientButton from '@/src/modules/build-a-card/DeleteRecipientButton'
import { useGiftQuantityContext } from '@/src/common/contexts/GiftQuantityContext'
import { ValidationError } from 'yup'
import {
    singleRecipientEmailSchema,
    singleRecipientNameSchema,
    singleRecipientPhoneSchema,
} from '@/src/modules/build-a-card/validations/schemas'
import PhoneNumberInput from '@/src/common/components/elements/Inputs/PhoneNumberInput'
import EmailInput from '@/src/common/components/elements/Inputs/EmailInput'
import NameInput from '@/src/common/components/elements/Inputs/NameInput'
import { TEXT_DELIVERY } from '../../pg-plus/constants'
import { styled } from '@/src/stitches.config'

const FieldsWrapper = styled('div', {
    display: 'flex',
    fd: 'column',
    gap: 15,
})

type TRecipientProps = {
    recipient: TRecipientInfo
    recipientIndex: number
    hasMoreThanOneRecipient: boolean
}

const RecipientInformation = ({
    recipient,
    recipientIndex,
    hasMoreThanOneRecipient,
}: TRecipientProps) => {
    const { giftState, displayLiveErrors, dispatch } = useGiftQuantityContext()
    const {
        listOfRecipients,
        setListOfRecipients,
        sendingViaEmail,
        deliveryType,
    } = giftState

    const {
        recipientName,
        recipientEmail,
        recipientPhone,
        recipientErrorMessages,
    } = recipient
    const indexForDisplay = recipientIndex + 1
    const [formErrorsEmail, setFormErrorsEmail] = useState<
        ValidationError[] | null
    >([])
    const [formErrorsPhone, setFormErrorsPhone] = useState<
        ValidationError[] | null
    >([])
    const [formErrorsName, setFormErrorsName] = useState<
        ValidationError[] | null
    >([])

    const multipleNamesLabel = `Recipient ${indexForDisplay}'s name`
    const multipleEmailLabel = `Recipient ${indexForDisplay}'s email address`
    const multiplePhoneLabel = `Recipient ${indexForDisplay}'s phone number`

    const validateBasedOnDeliveryType = (
        deliveryType: string,
        listOfRecipients: TRecipientInfo[],
        recipientIndex: number,
        phone?: string,
        email?: string
    ) => {
        if (deliveryType === TEXT_DELIVERY) {
            const formattedPhone = String(phone).replace(/\D/g, '')
            const validation = {
                recipientPhone: phone,
            }
            singleRecipientPhoneSchema
                .validate(validation, {
                    abortEarly: false,
                    context: {
                        checkPhoneBlocked: formattedPhone.length === 10,
                    },
                })
                .then(() => {
                    setFormErrorsPhone(null)
                    listOfRecipients[recipientIndex].recipientHasError = false
                })
                .catch((err) => {
                    if (displayLiveErrors) {
                        setFormErrorsPhone(err.inner)
                    }
                    listOfRecipients[recipientIndex].recipientHasError = true
                })
        } else {
            const validation = {
                recipientEmail: email,
            }

            singleRecipientEmailSchema
                .validate(validation, {
                    abortEarly: false,
                    context: {
                        checkPhoneBlocked: false,
                    },
                })
                .then(() => {
                    setFormErrorsEmail(null)
                    listOfRecipients[recipientIndex].recipientHasError = false
                })
                .catch((err) => {
                    if (displayLiveErrors) {
                        setFormErrorsEmail(err.inner)
                    }
                    listOfRecipients[recipientIndex].recipientHasError = true
                })
        }
    }

    useEffect(() => {
        if (deliveryType !== TEXT_DELIVERY) {
            setFormErrorsPhone(null)
            listOfRecipients[recipientIndex].recipientHasError = false
        } else {
            setFormErrorsEmail(null)
            listOfRecipients[recipientIndex].recipientHasError = false
        }

        validateBasedOnDeliveryType(
            deliveryType,
            listOfRecipients,
            recipientIndex,
            recipientPhone,
            recipientEmail
        )
    }, [
        recipientPhone,
        recipientEmail,
        sendingViaEmail,
        deliveryType,
        dispatch,
        recipientErrorMessages,
        displayLiveErrors,
    ])

    useEffect(() => {
        const formattedPhone = String(recipientPhone).replace(/\D/g, '')
        const validation = {
            recipientName: recipientName,
        }
        singleRecipientNameSchema
            .validate(validation, {
                abortEarly: false,
                context: {
                    checkPhoneBlocked: formattedPhone.length === 10,
                },
            })
            .then(() => {
                setFormErrorsName(null)
                listOfRecipients[recipientIndex].recipientHasError = false
            })
            .catch((err) => {
                if (displayLiveErrors) {
                    setFormErrorsName(err.inner)
                }
                listOfRecipients[recipientIndex].recipientHasError = true
            })
    }, [
        recipientName,
        recipientPhone,
        sendingViaEmail,
        deliveryType,
        recipientErrorMessages,
        displayLiveErrors,
    ])

    return (
        <>
            <FieldsWrapper>
                <NameInput
                    name={recipientName}
                    setName={() => {}}
                    formErrors={formErrorsName ?? []}
                    setFormErrors={setFormErrorsName}
                    fieldId={'recipientName'}
                    handleList={{
                        key: 'recipientName',
                        listOfRecipients: listOfRecipients,
                        setListOfRecipients: setListOfRecipients,
                        recipientIndex: recipientIndex,
                    }}
                    label={
                        hasMoreThanOneRecipient ? multipleNamesLabel : 'Name'
                    }
                    placeholder={
                        hasMoreThanOneRecipient
                            ? multipleNamesLabel
                            : 'Enter a recipient name'
                    }
                    isRequired={true}
                />
                {!sendingViaEmail ? (
                    <PhoneNumberInput
                        isRequired={true}
                        phone={recipientPhone}
                        setPhone={() => {}}
                        formErrors={formErrorsPhone ?? []}
                        setFormErrors={setFormErrorsPhone}
                        fieldId={'recipientPhone'}
                        handleList={{
                            key: 'recipientPhone',
                            listOfRecipients: listOfRecipients,
                            setListOfRecipients: setListOfRecipients,
                            recipientIndex: recipientIndex,
                        }}
                        label={
                            hasMoreThanOneRecipient
                                ? multiplePhoneLabel
                                : 'Phone number'
                        }
                        placeholder={
                            hasMoreThanOneRecipient
                                ? multiplePhoneLabel
                                : 'Enter a phone number'
                        }
                    />
                ) : (
                    <EmailInput
                        isRequired={true}
                        email={recipientEmail}
                        setEmail={() => {}}
                        formErrors={formErrorsEmail ?? []}
                        setFormErrors={setFormErrorsEmail}
                        fieldId={'recipientEmail'}
                        handleList={{
                            key: 'recipientEmail',
                            listOfRecipients: listOfRecipients,
                            setListOfRecipients: setListOfRecipients,
                            recipientIndex: recipientIndex,
                        }}
                        label={
                            hasMoreThanOneRecipient
                                ? multipleEmailLabel
                                : 'Email address'
                        }
                        placeholder={
                            hasMoreThanOneRecipient
                                ? multipleEmailLabel
                                : 'Enter an email address'
                        }
                    />
                )}
            </FieldsWrapper>
            {hasMoreThanOneRecipient && (
                <DeleteRecipientButton recipientIndex={recipientIndex} />
            )}
        </>
    )
}

export default RecipientInformation
